<template>
  <v-container class="pa-0">
    <div class="flex-container">
      <v-card id="review-card" class="mb-auto">
        <div class="work-details">
          <img :src="review.work.posterSource" :alt="review.work.title + ' poster'" />
          <div>
            <h1>{{ review.work.title }}</h1>
            <h2 v-if="workDetails.creators">{{ workDetails.creators.map((creator) => creator.name).join(",") }}</h2>
          </div>
        </div>

        <div id="review-header">
          <div>
            <router-link :to="{ name: 'profile', params: { username: review.author.username } }">
              <div class="d-flex align-center">
                <profile-avatar
                  :username="review.author.username"
                  :size="48"
                  :preset-profile-pic="review.author.profilePicture"
                ></profile-avatar>
                <h2 class="ml-2">{{ review.author.username }}</h2>
              </div>
            </router-link>
            <button @click="toggleLiked" :disabled="$store.state.username === review.author.username" class="ml-13">
              <v-icon :color="review.likedByMe ? 'primaryText' : 'icon'">mdi-heart</v-icon>
              <span class="ml-1">{{ review.likes.totalCount }} like{{ review.likes.totalCount == 1 ? "" : "s" }}</span>
            </button>
          </div>

          <div v-if="editMode">
            <menu-date-picker v-model="edit.dateViewed" :disabled="false" :max="nowIso" :clearable="true" />
          </div>
          <div v-else-if="review.dateViewed">
            <v-icon>mdi-calendar-clock</v-icon>
            {{ new Date(review.dateViewed).toUTCString().slice(0, -13) }}
            <!-- May Break for production -->
          </div>
        </div>

        <v-rating
          v-if="editMode"
          v-model="edit.rating"
          background-color="primary"
          color="secondary"
          hover
          length="10"
          dense
          :size="vRatingSize"
          class="d-flex justify-center"
        />

        <div v-if="!editMode">
          <rating-circle
            v-if="review.rating"
            :score="review.rating"
            :diameter="$vuetify.breakpoint.xs ? 75 : 192"
            class="float-right ml-2"
          ></rating-circle>
          <p class="review-text">{{ review.review }}</p>
        </div>
        <div v-else>
          <textarea id="edit-review-text" v-model="edit.review" @input="expandTextArea()"></textarea>
          <v-checkbox v-model="edit.spoiler" label="Contains Spoilers" />
          <v-btn @click="updateReview" color="primary" class="float-right">Save</v-btn>
        </div>
        <div>
          <v-btn
            v-if="!editMode && $store.state.username === review.author.username"
            @click="editMode = !editMode"
            color="primary"
            >Edit</v-btn
          >
          <v-btn v-else-if="editMode" @click="discardChanges" color="primary">Discard</v-btn>
        </div>
      </v-card>

      <v-card id="comments-card">
        <h2>Comments</h2>
        <div v-for="comment in review.comments.items" :key="comment.id">
          <v-divider></v-divider>
          <profile-avatar
            :username="comment.author.username"
            :size="32"
            class="ml-0 mr-2 mt-2 float-left"
          ></profile-avatar>

          <!-- Comment options -->
          <comment-utility-button :size="21" :comment="comment" :review="review"></comment-utility-button>

          <h3 class="comment-username">{{ comment.username }}</h3>
          <p>{{ comment.content }}</p>
        </div>

        <div>
          <!-- Include textarea to post a new comment -->
          <v-divider class="pb-2"></v-divider>
          <p v-if="!username" class="login-hint">Log in to post a comment</p>
          <textarea v-model="newComment" id="comment-textarea" rows="4" :disabled="!username"></textarea>
          <v-btn @click="postComment" color="primary" class="float-right" :disabled="!username">Post</v-btn>
        </div>
      </v-card>
    </div>
  </v-container>
</template>

<script>
//components
import ProfileAvatar from "../components/profile-avatar.vue";
import ratingCircle from "../components/rating-circles/rating-circle-legacy.vue";
import MenuDatePicker from "../components/menu-date-picker.vue";
import commentUtilityButton from "../components/comment-utility-button.vue";
//mixins
import getReview from "../mixins/getReview";

import { mapMutations } from "vuex";
import { toLocaleISO } from "../util/dateHelpers";

export default {
  name: "ReviewPage",

  components: {
    ProfileAvatar,
    ratingCircle,
    MenuDatePicker,
    commentUtilityButton,
  },

  mixins: [getReview],

  data: () => {
    return {
      review: {
        rating: undefined,
        review: undefined,
        work: {},
        author: {},
        comments: {},
        likedByMe: false,
        likes: {
          totalCount: undefined,
        },
      },
      workDetails: {},

      editMode: false,
      edit: {
        rating: undefined, //Integer
        review: undefined, //String
        spoiler: undefined, //Boolean
        dateViewed: undefined,
      },

      newComment: "",
    };
  },

  computed: {
    medium() {
      return this.review.work.medium;
    },
    vRatingSize() {
      return this.$vuetify.breakpoint.xs ? 23 : 50;
    },
    username() {
      return this.$store.state.username;
    },
    nowIso() {
      return this.toLocaleISO(new Date());
    },
  },

  async mounted() {
    this.$root.$on("delete-comment", this.handleDeleteComment);

    this.review = await this.getReview(this.$route.params.id);
    console.log(this.review);
    this.edit.rating = this.review.rating;
    this.edit.review = this.review.review;
    this.edit.spoiler = this.review.spoiler;
    if (this.review.dateViewed) this.edit.dateViewed = new Date(this.review.dateViewed).toISOString().slice(0, 10);
    console.log(this.edit.dateViewed);

    this.workDetails = await this.getWorkDetails(this.review.work.medium, undefined, this.review.work.id);

    if (this.$route.query.edit && this.$store.state.username === this.review.author.username) {
      this.editMode = true;
      this.expandTextArea();
    }
  },

  methods: {
    toLocaleISO,
    ...mapMutations(["showSnackbar"]),

    updateReview: async function () {
      this.review.review = this.edit.review;
      this.review.rating = this.edit.rating;
      this.review.spoiler = this.edit.spoiler;
      this.review.dateViewed = this.edit.dateViewed;

      let patch = {
        review_id: this.$route.params.id,
        review: this.review.review,
        rating: this.review.rating,
        // '+' Converts boolean into integer counterpart
        spoiler: +this.review.spoiler,
        date_viewed: this.review.dateViewed,
      };
      let response = await this.$axios.patch("/review", patch);

      if (response.data === 0) {
        this.showSnackbar({ text: "An Error occurred. Review not updated" });
        this.review = await this.getReview(this.$route.params.id);
      } else {
        this.editMode = false;
      }
    },

    expandTextArea: function () {
      /* Code from https://stackoverflow.com/questions/2803880/is-there-a-way-to-get-a-textarea-to-stretch-to-fit-its-content-without-using-php */
      let textArea = document.getElementById("edit-review-text");
      if (!textArea) return;
      textArea.style.height = "";
      textArea.style.height = textArea.scrollHeight + 4 + "px";
    },

    discardChanges: function () {
      this.edit = {
        rating: this.review.rating,
        review: this.review.review,
        spoiler: this.review.spoiler,
      };

      this.editMode = false;
    },

    postComment: function () {
      this.$axios
        .post("/comment", {
          review_id: this.$route.params.id,
          content: this.newComment,
        })
        .then((response) => {
          let comment = response.data;
          this.review.comments.items.push(this.v1FormattedComment(comment));
          this.newComment = "";
        });
    },

    handleDeleteComment(comment) {
      const indexOfComment = this.review.comments.items.findIndex((c) => c.id === comment.id);
      if (indexOfComment < 0) return;
      this.$delete(this.review.comments.items, indexOfComment);
    },

    async getWorkDetails() {
      const response = await this.$axios.get(`/v1/works/${this.review.work.medium}/${this.review.work.id}`);
      return response.data;
    },

    v1FormattedComment(comment) {
      return {
        id: comment.id,
        reviewId: comment.review_id,
        author: {
          id: comment.commenter_user_id,
          username: comment.username,
        },
        content: comment.content,
        timePosted: comment.time_posted,
      };
    },

    toggleLiked() {
      if (this.review.likedByMe) {
        this.unlikeReview(this.review.id);
        this.review.likes.totalCount -= 1;
      } else {
        this.likeReview(this.review.id);
        this.review.likes.totalCount += 1;
      }
      this.review.likedByMe = !this.review.likedByMe;
    },
  },

  watch: {
    editMode: function (val) {
      if (val) {
        this.$nextTick(() => {
          this.expandTextArea();
        });
      }
    },
  },
};
</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
}
@media (max-width: 939px) {
  .flex-container {
    flex-direction: column;
  }
  #comments-card {
    margin-top: 12px;
    width: 100%;
  }
}

p {
  white-space: pre-wrap;
}

#review-card,
#comments-card {
  padding: 12px;
}

.work-details {
  display: flex;
}
.work-details > img {
  height: 100px;
  max-width: 100px;
  margin: 0 8px 8px 0;
}
@media (min-width: 768px) {
  .work-details > img {
    height: 300px;
    max-width: 300px;
  }
}

#review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-top: 8px;
  margin-bottom: 4px;

  width: calc(100% + 16px);
  transform: translateX(-8px);
  padding: 4px 8px;
  border-radius: 10px 10px 0 0;
}
#review-header a {
  color: var(--v-text-base);
}

.theme--light #review-header {
  background: var(--v-card-darken1);
}
.theme--dark #review-header {
  background: var(--v-card-lighten1);
}

#edit-review-text,
#comment-textarea {
  color: var(--v-text-base);
  border: 1px solid var(--v-text-base);
  border-radius: 5px;
  width: 100%;
  padding: 0 4px;
}

@media (min-width: 940px) {
  #comments-card {
    width: 25%;
    min-width: 300px;
  }

  #review-card {
    flex-grow: 1;
    margin-right: 20px;
  }
}

.comment-username {
  font-size: 32px;
  font-weight: normal;
}

.comment-utility-button {
  display: inline-block;
  margin-top: 12px;
  float: right;
}

.login-hint {
  font-size: 14px;
  margin-bottom: 4px;
  color: var(--v-text-lighten1);
}
</style>
